import { useState } from "react";
import { FORMS, fieldsOf, indexGroups, toolStatus } from "./engine/forms.ts";
import type { FieldDef, FormDef } from "./engine/model.ts";

export default function App() {
  const [selected, setSelected] = useState<FormDef | null>(null);
  return (
    <div className="app">
      <header className="topbar">
        <img src="/logo.png" alt="Fort Energy" className="logo" />
        <div className="topbar-text">
          <strong>On-site energy audit</strong>
          <span className="version">{__APP_VERSION__}</span>
        </div>
      </header>
      {selected ? <FormPreview form={selected} onBack={() => setSelected(null)} /> : <Index onPick={setSelected} />}
    </div>
  );
}

function Index({ onPick }: { onPick: (f: FormDef) => void }) {
  const c = FORMS.counts;
  return (
    <main className="content">
      <p className="lead">
        Forms generated from <code>{FORMS.source.file}</code>: {c.forms} forms, {c.fields.toLocaleString()} data points ({c.calc} calculated),{" "}
        {c.dropdowns} dropdowns, {c.withTool} readings naming {c.instruments} instruments, {c.asks} client requests.
      </p>
      {indexGroups().map((g) => (
        <section key={g.title} className="group">
          <h2>{g.title}</h2>
          <ul className="form-list">
            {g.entries.map((e) => (
              <li key={e.number}>
                {e.form ? (
                  <button className="form-button" onClick={() => onPick(e.form!)}>
                    <span className="num">{e.number}</span>
                    <span className="name">{e.name}</span>
                    <span className="what">{e.what}</span>
                    {e.toolsMissing && <span className="missing">Needs: {e.toolsMissing}</span>}
                  </button>
                ) : (
                  <div className="form-button disabled">
                    <span className="num">{e.number}</span>
                    <span className="name">{e.name}</span>
                    <span className="what">{e.what}</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </main>
  );
}

function FormPreview({ form, onBack }: { form: FormDef; onBack: () => void }) {
  const fields = fieldsOf(form);
  return (
    <main className="content">
      <button className="back" onClick={onBack}>
        ◀ Index
      </button>
      <h1>
        {form.number} {form.title}
      </h1>
      <p className="intro">{form.intro}</p>
      <p className="meta">
        Items: {form.itemIds[0]} … {form.itemIds[form.itemIds.length - 1]} ({form.itemIds.length}) · Feeds: {form.feeds} · {fields.length} data points
      </p>
      {form.sections.map((s) => (
        <section key={s.row} className="section">
          <h2>
            {s.title}
            {s.enterOnce && <span className="once"> enter once</span>}
          </h2>
          {s.fields.map((f) => (
            <Field key={f.ref} field={f} />
          ))}
        </section>
      ))}
      {form.asks.length > 0 && (
        <section className="section asks">
          <h2>Ask the client</h2>
          <ul>
            {form.asks.map((a) => (
              <li key={a.ref}>
                <strong>{a.ref}</strong> {a.text}
                {a.why && <span className="why"> — {a.why}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}

function Field({ field }: { field: FieldDef }) {
  const [open, setOpen] = useState(false);
  const status = toolStatus(field.tool);
  const list = field.list ? FORMS.lists[field.list] : undefined;
  return (
    <div className={"field" + (field.calc ? " calc" : "")}>
      <div className="field-head">
        <span className="ref">{field.ref}</span>
        <span className="label">{field.label}</span>
        {field.unit && <span className="unit">{field.unit}</span>}
        {field.kind === "list" && <span className="unit">▼</span>}
        {field.tool && <span className={"tool " + (status ?? "unknown")}>{field.tool}</span>}
        {field.flag && <span className="flag">flag {field.flag.text}</span>}
        {(field.tip || field.example !== null) && (
          <button className="info" onClick={() => setOpen(!open)} aria-label="Tip">
            ⓘ
          </button>
        )}
      </div>
      {open && (
        <div className="tip">
          {field.tip && <p>{field.tip}</p>}
          {field.example !== null && !field.calc && (
            <p className="example">
              e.g. <code>{String(field.example)}</code>
            </p>
          )}
          {list && <p className="options">Options: {list.join(" · ")}</p>}
          {field.dependentOn && <p className="options">Material list follows {field.dependentOn}</p>}
          {field.calc && <p className="options">Calculated: {field.calc.formula}</p>}
        </div>
      )}
    </div>
  );
}
